import apiClient from "@/api-client";
import { toast } from "@/components/core/toaster";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { campaignsKeys } from "./use-campaigns";
import { agentsKeys } from "./use-agents";

export interface Tool {
	name: string;
	description: string;
  title: string;
	fields: Record<string, any>;
  /** Default for `allowInterruption` when the tool is not configured yet. */
  allowInterruption?: boolean;
  llm: {
    name: string;
    description: string;
    parameters: any;
  }
}

export interface CampaignTool {
	name: string;
	description: string;
	allowInterruption: boolean;
	enabled: boolean;
	settings: Record<string, any>;
}

export const toolsKeys = {
  all: ['tools'] as const,
};

export type ToolScope = 'agents' | 'campaigns';

export function useScopedTools(scope: ToolScope, id: string) {
  return useQuery({
    queryKey: [...toolsKeys.all, scope, id],
    queryFn: async (): Promise<CampaignTool[]> => {
      const response = await apiClient.get(`/${scope}/${id}/tools?offset=0&limit=100`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useInheritedTools(scope: ToolScope, id: string) {
  return useQuery({
    queryKey: [...toolsKeys.all, scope, id, 'inherited'],
    queryFn: async (): Promise<CampaignTool[]> => {
      const response = await apiClient.get(`/campaigns/${id}/tools/inherited`);
      return response.data;
    },
    enabled: scope === 'campaigns' && !!id,
  });
}

export function useUpdateScopedTool(scope: ToolScope, id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, data }: { name: string; data?: CampaignTool }) => {
      if (!data) return apiClient.delete(`/${scope}/${id}/tools/${name}`);
      return apiClient.put(`/${scope}/${id}/tools/${name}`, data);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: toolsKeys.all });
      await client.invalidateQueries({ queryKey: scope === 'agents' ? agentsKeys.detail(id) : campaignsKeys.detail(id) });
      if (scope === 'campaigns') await client.invalidateQueries({ queryKey: campaignsKeys.summarySchema(id) });
      toast.success('Tool updated successfully');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Unable to update the tool'),
  });
}

export function useTools() {
  return useQuery({
    queryKey: toolsKeys.all,
    queryFn: async () => {
      const response = await apiClient.get(`/tools`);
      return response.data;
    },
  });
}
